import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch, BASE_URL } from '../config/api';
import { useLanguage } from './LanguageContext';

const UOMContext = createContext();

export const useUOM = () => {
  const context = useContext(UOMContext);
  if (!context) {
    throw new Error('useUOM must be used within a UOMProvider');
  }
  return context;
};

export const UOMProvider = ({ children }) => {
  const [uoms, setUoms] = useState([]);
  const [uomsByCategory, setUomsByCategory] = useState({
    QUANTITY: [],
    WEIGHT: [],
    LENGTH: [],
    AREA: [],
    VOLUME: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentLanguage } = useLanguage();

  // Fetch all UOMs from the API
  const fetchUOMs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`${BASE_URL}/api/uom`);
      if (!response.ok) {
        throw new Error('Failed to fetch UOMs');
      }

      const data = await response.json();
      setUoms(data.uoms || []);

      // Also fetch by category
      const categoryResponse = await apiFetch(`${BASE_URL}/api/uom/categories`);
      if (categoryResponse.ok) {
        const categoryData = await categoryResponse.json();
        setUomsByCategory(categoryData);
      }
    } catch (err) {
      console.error('Error fetching UOMs:', err);
      setError(err.message);
      // Set default fallback UOMs if API fails
      setUoms(getDefaultUOMs());
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch UOMs on mount
  useEffect(() => {
    fetchUOMs();
  }, [fetchUOMs]);

  // Get the display label for a UOM code (with language support)
  const getUOMLabel = useCallback((uomCode) => {
    if (!uomCode) return '';

    const uom = uoms.find(u => u.uom_code === uomCode.toUpperCase());
    if (!uom) return uomCode;

    // Return Spanish name if available and language is Spanish
    if (currentLanguage === 'es' && uom.uom_name_es) {
      return uom.uom_name_es;
    }
    return uom.uom_name;
  }, [uoms, currentLanguage]);

  // Get the symbol for a UOM code
  const getUOMSymbol = useCallback((uomCode) => {
    if (!uomCode) return '';

    const uom = uoms.find(u => u.uom_code === uomCode.toUpperCase());
    return uom?.symbol || uomCode;
  }, [uoms]);

  // Get UOM object by code
  const getUOMByCode = useCallback((uomCode) => {
    if (!uomCode) return null;
    return uoms.find(u => u.uom_code === uomCode.toUpperCase()) || null;
  }, [uoms]);

  // Get UOMs for a specific category
  const getUOMsByCategory = useCallback((category) => {
    return uomsByCategory[category] || [];
  }, [uomsByCategory]);

  // Format a value with its UOM
  const formatWithUOM = useCallback((value, uomCode, showSymbol = false) => {
    if (value === null || value === undefined) return '';

    const uom = getUOMByCode(uomCode);
    if (!uom) return `${value} ${uomCode || ''}`.trim();

    if (showSymbol && uom.symbol) {
      return `${value} ${uom.symbol}`;
    }
    return `${value} ${getUOMLabel(uomCode)}`;
  }, [getUOMByCode, getUOMLabel]);

  // Get all UOMs as options for select dropdowns
  const getUOMOptions = useCallback(() => {
    return uoms.map(uom => ({
      value: uom.uom_code,
      label: currentLanguage === 'es' && uom.uom_name_es ? uom.uom_name_es : uom.uom_name,
      symbol: uom.symbol,
      category: uom.uom_category
    }));
  }, [uoms, currentLanguage]);

  // Get UOM options grouped by category for select dropdowns
  const getUOMOptionsGrouped = useCallback(() => {
    const categories = ['QUANTITY', 'WEIGHT', 'LENGTH', 'AREA', 'VOLUME'];
    const categoryLabels = {
      QUANTITY: currentLanguage === 'es' ? 'Cantidad' : 'Quantity',
      WEIGHT: currentLanguage === 'es' ? 'Peso' : 'Weight',
      LENGTH: currentLanguage === 'es' ? 'Longitud' : 'Length',
      AREA: currentLanguage === 'es' ? 'Área' : 'Area',
      VOLUME: currentLanguage === 'es' ? 'Volumen' : 'Volume'
    };

    return categories.map(category => ({
      label: categoryLabels[category],
      options: (uomsByCategory[category] || []).map(uom => ({
        value: uom.uom_code,
        label: currentLanguage === 'es' && uom.uom_name_es ? uom.uom_name_es : uom.uom_name,
        symbol: uom.symbol
      }))
    })).filter(group => group.options.length > 0);
  }, [uomsByCategory, currentLanguage]);

  const value = {
    uoms,
    uomsByCategory,
    loading,
    error,
    getUOMLabel,
    getUOMSymbol,
    getUOMByCode,
    getUOMsByCategory,
    formatWithUOM,
    getUOMOptions,
    getUOMOptionsGrouped,
    refreshUOMs: fetchUOMs
  };

  return (
    <UOMContext.Provider value={value}>
      {children}
    </UOMContext.Provider>
  );
};

// Default UOMs as fallback if API is unavailable
const getDefaultUOMs = () => [
  { uom_code: 'PCS', uom_name: 'Pieces', uom_name_es: 'Piezas', uom_category: 'QUANTITY', symbol: 'pcs' },
  { uom_code: 'UNIT', uom_name: 'Unit', uom_name_es: 'Unidad', uom_category: 'QUANTITY', symbol: 'u' },
  { uom_code: 'SET', uom_name: 'Set', uom_name_es: 'Juego', uom_category: 'QUANTITY', symbol: 'set' },
  { uom_code: 'KG', uom_name: 'Kilogram', uom_name_es: 'Kilogramo', uom_category: 'WEIGHT', symbol: 'kg' },
  { uom_code: 'G', uom_name: 'Gram', uom_name_es: 'Gramo', uom_category: 'WEIGHT', symbol: 'g' },
  { uom_code: 'LB', uom_name: 'Pound', uom_name_es: 'Libra', uom_category: 'WEIGHT', symbol: 'lb' },
  { uom_code: 'M', uom_name: 'Meter', uom_name_es: 'Metro', uom_category: 'LENGTH', symbol: 'm' },
  { uom_code: 'CM', uom_name: 'Centimeter', uom_name_es: 'Centímetro', uom_category: 'LENGTH', symbol: 'cm' },
  { uom_code: 'MM', uom_name: 'Millimeter', uom_name_es: 'Milímetro', uom_category: 'LENGTH', symbol: 'mm' },
  { uom_code: 'L', uom_name: 'Liter', uom_name_es: 'Litro', uom_category: 'VOLUME', symbol: 'L' },
  { uom_code: 'ML', uom_name: 'Milliliter', uom_name_es: 'Mililitro', uom_category: 'VOLUME', symbol: 'ml' },
  { uom_code: 'SQM', uom_name: 'Square Meter', uom_name_es: 'Metro Cuadrado', uom_category: 'AREA', symbol: 'm²' }
];

export default UOMContext;
